import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { articles, getArticleBySlug } from '@/mocks/articles';
import { movies, getMovieBySlug } from '@/mocks/movies';

export default function ArticlePage() {
  const { id = '' } = useParams<{ id: string }>();

  const article = getArticleBySlug(id);
  const movie = getMovieBySlug(id);

  if (!article && !movie) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-900 mb-2">内容未找到</h2>
            <p className="text-foreground-500 mb-6">您访问的内容不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (movie) {
    return (
      <>
        <SEOHead
          title={`${movie.title} ${movie.titleEn} HD在线观看 | 光影在线`}
          description={movie.description.slice(0, 150)}
          keywords={`${movie.title},${movie.title}在线观看,${movie.title}免费观看,${movie.title}高清,${movie.category}电影`}
          canonicalUrl={`https://guangyingzx.com/article/${movie.slug}`}
          ogImage={movie.poster}
          ogType="video.movie"
          schemaJson={{
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            description: movie.description,
            image: movie.poster,
            dateCreated: `${movie.year}`,
            director: { '@type': 'Person', name: movie.director },
            actor: movie.cast.map((name) => ({ '@type': 'Person', name })),
            duration: movie.duration,
          }}
        />
        <ScrollToTop />
        <Navbar />
        <main className="pt-20">
          <article className="px-4 md:px-6 py-8">
            <div className="max-w-4xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
                <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to={`/category/${movie.category}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">{movie.category}</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-primary-600 font-medium whitespace-nowrap">{movie.title}</span>
              </nav>

              <div className="flex flex-col md:flex-row gap-8">
                <div className="md:w-72 shrink-0">
                  <div className="aspect-[2/3] rounded-xl overflow-hidden">
                    <img
                      src={movie.poster}
                      alt={movie.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                    {movie.title}
                  </h1>
                  <p className="text-sm text-foreground-500 mb-6">{movie.titleEn}</p>

                  <div className="flex flex-wrap items-center gap-3 mb-6">
                    <span className="px-3 py-1 rounded-md bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap">
                      {movie.rating} 分
                    </span>
                    <span className="text-sm text-foreground-600">{movie.year}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                    <span className="text-sm text-foreground-600">{movie.duration}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                    <span className="text-sm text-foreground-600">{movie.category}</span>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-foreground-500 shrink-0 whitespace-nowrap">导演：</span>
                      <span className="text-sm text-foreground-800">{movie.director}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-foreground-500 shrink-0 whitespace-nowrap">主演：</span>
                      <span className="text-sm text-foreground-800">{movie.cast.join(' / ')}</span>
                    </div>
                  </div>

                  <div className="bg-background-100 rounded-xl p-5 mb-6">
                    <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-3">剧情简介</h3>
                    <p className="text-sm text-foreground-700 leading-relaxed">{movie.description}</p>
                  </div>

                  <div className="flex gap-3">
                    <button className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-play-circle-line text-lg"></i>
                      立即播放
                    </button>
                    <button className="flex items-center gap-2 px-6 py-3 rounded-full bg-background-100 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer border border-background-200">
                      <i className="ri-heart-line"></i>
                      收藏
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </article>

          <section className="px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-product-shop>
                {movies
                  .filter((m) => m.category === movie.category && m.id !== movie.id)
                  .slice(0, 4)
                  .map((m) => (
                    <Link
                      key={m.id}
                      to={`/article/${m.slug}`}
                      className="group cursor-pointer"
                    >
                      <div className="aspect-[2/3] rounded-lg overflow-hidden mb-2">
                        <img
                          src={m.poster}
                          alt={m.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">{m.title}</h3>
                      <span className="text-xs text-foreground-500">{m.rating}分</span>
                    </Link>
                  ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${article.title} | 光影在线`}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://guangyingzx.com/article/${article.slug}`}
        ogImage={article.image}
        ogType="article"
        schemaJson={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.summary,
          image: article.image,
          datePublished: article.date,
          author: { '@type': 'Person', name: article.author },
        }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20">
        <article className="px-4 md:px-6 py-8">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-primary-600 font-medium whitespace-nowrap">{article.category}</span>
            </nav>

            <div className="mb-6">
              <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-700 text-xs font-medium">
                {article.category}
              </span>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mb-8 text-sm text-foreground-500">
              <span><i className="ri-user-line mr-1"></i>{article.author}</span>
              <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
              <span><i className="ri-eye-line mr-1"></i>{article.readCount.toLocaleString()} 阅读</span>
            </div>

            <div className="aspect-[2/1] rounded-xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        <section className="px-4 md:px-6 py-12 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-lg font-bold text-foreground-900 mb-6">相关文章</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {articles
                .filter((a) => a.id !== article.id)
                .slice(0, 3)
                .map((a) => (
                  <Link
                    key={a.id}
                    to={`/article/${a.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={a.image}
                        alt={a.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">{a.title}</h3>
                      <p className="text-xs text-foreground-500 mt-2">{a.date}</p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}